import { useEffect, useState } from "react";
import { api, errorText } from "../api";
import { timeAgo } from "../format";
import { toast } from "../toast";
import { ACCENTS, type Accent, type KeyStatus, type Model, type Pronunciation, type TenantSettings } from "../types";

/* What used to be config.yaml.
   The single-tenant app had the streamer SSH into a box, edit a file and press
   "Reload config". There is no file any more and no operator to ask: this form
   writes the tenant's own row, and the server clamps whatever it is given, so
   the worst a typo can do is get folded back into range. */

/* Mirrors the server's defaults in settings.rs — a "Reset" here fills the form
   with exactly what the server would fall back to, nothing else. */
const DEFAULT_LIMITS: TenantSettings["limits"] = {
  max_chars: 300,
  cooldown_seconds: 15,
  daily_char_budget: 5000,
  max_queue: 20,
  history_retention_days: 14,
  min_credits_remaining: 0,
};
const DEFAULT_MODEL = "eleven_multilingual_v2";
const DEFAULT_FORMAT = "mp3_44100_128";

/* Only MP3: the server stores every clip as .mp3 and the overlay plays it in a
   browser source, so the pcm/ulaw formats ElevenLabs offers would be unplayable. */
const OUTPUT_FORMATS: { id: string; label: string }[] = [
  { id: "mp3_22050_32", label: "MP3 · 22 kHz · 32 kbps" },
  { id: "mp3_44100_32", label: "MP3 · 44.1 kHz · 32 kbps" },
  { id: "mp3_44100_64", label: "MP3 · 44.1 kHz · 64 kbps" },
  { id: "mp3_44100_96", label: "MP3 · 44.1 kHz · 96 kbps" },
  { id: "mp3_44100_128", label: "MP3 · 44.1 kHz · 128 kbps (default)" },
  { id: "mp3_44100_192", label: "MP3 · 44.1 kHz · 192 kbps (Creator+)" },
];

interface Props {
  settings: TenantSettings | null;
  models: Model[];
  keyStatus: KeyStatus | null;
  onSaved: (settings: TenantSettings) => void;
  onKeySaved: (status: KeyStatus) => void;
}

function SectionHead({ title, onReset }: { title: string; onReset?: () => void }) {
  return (
    <div className="section-head">
      <h3>{title}</h3>
      {onReset && (
        <button type="button" className="ghost" onClick={onReset}>
          Reset to defaults
        </button>
      )}
    </div>
  );
}

/* The server folds every one of these back into range on write. Saying so up
   front turns a value that silently changed on save into one the form refused
   to accept in the first place. */
interface NumberFieldProps {
  label: string;
  value: number;
  min: number;
  max?: number;
  hint?: string;
  onChange: (value: number) => void;
}

function NumberField({ label, value, min, max, hint, onChange }: NumberFieldProps) {
  const range = max === undefined ? `${min} or more` : `${min}–${max}`;
  return (
    <label>
      {label} <small>{hint ? `${hint} · ${range}` : range}</small>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

export function SettingsForm({
  settings,
  models,
  keyStatus,
  onSaved,
  onKeySaved,
}: Props) {
  const [draft, setDraft] = useState<TenantSettings | null>(settings);
  const [saving, setSaving] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [savingKey, setSavingKey] = useState(false);

  useEffect(() => setDraft(settings), [settings]);

  if (!draft || !settings) return null;

  const limits = draft.limits;
  const patchLimits = (patch: Partial<TenantSettings["limits"]>) =>
    setDraft({ ...draft, limits: { ...limits, ...patch } });
  const patchEleven = (patch: Partial<TenantSettings["elevenlabs"]>) =>
    setDraft({ ...draft, elevenlabs: { ...draft.elevenlabs, ...patch } });

  const model = draft.elevenlabs.default_model;
  const format = draft.elevenlabs.output_format;

  /* Structural comparison against what the server last confirmed. The form is a
     handful of scalars and two string arrays, so this is cheap and, unlike a
     per-field dirty flag, cannot drift out of step with the fields. */
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const save = async () => {
    setSaving(true);
    try {
      // The server answers with what it actually stored, which may differ from
      // what was sent — show that, rather than leaving a rejected value on screen.
      const saved = await api<TenantSettings>("/api/admin/settings", {
        method: "PUT",
        body: JSON.stringify(draft),
      });
      setDraft(saved);
      onSaved(saved);
      toast("Settings saved", "ok");
    } catch (error) {
      toast(errorText(error), "error");
    } finally {
      setSaving(false);
    }
  };

  const saveKey = async () => {
    const api_key = keyInput.trim();
    if (!api_key) return;
    setSavingKey(true);
    try {
      // Verified against ElevenLabs before it is stored, so a bad paste fails
      // here rather than on the next message.
      const result = await api<{ key: KeyStatus }>("/api/admin/elevenlabs-key", {
        method: "PUT",
        body: JSON.stringify({ api_key }),
      });
      setKeyInput("");
      onKeySaved(result.key);
      toast("API key updated", "ok");
    } catch (error) {
      toast(errorText(error), "error");
    } finally {
      setSavingKey(false);
    }
  };

  return (
    <div className="settings-form">
      <SectionHead
        title="Limits"
        onReset={() => setDraft({ ...draft, limits: { ...DEFAULT_LIMITS } })}
      />
      <NumberField
        label="Max characters per message"
        value={limits.max_chars}
        min={1}
        max={5000}
        onChange={(max_chars) => patchLimits({ max_chars })}
      />
      <NumberField
        label="Cooldown between messages"
        hint="seconds"
        value={limits.cooldown_seconds}
        min={0}
        max={3600}
        onChange={(cooldown_seconds) => patchLimits({ cooldown_seconds })}
      />
      <NumberField
        label="Daily characters per person"
        hint="0 = unlimited"
        value={limits.daily_char_budget}
        min={0}
        onChange={(daily_char_budget) => patchLimits({ daily_char_budget })}
      />
      <NumberField
        label="Queue depth"
        value={limits.max_queue}
        min={1}
        max={200}
        onChange={(max_queue) => patchLimits({ max_queue })}
      />
      <NumberField
        label="Keep history for"
        hint="days, 0 = forever"
        value={limits.history_retention_days}
        min={0}
        max={3650}
        onChange={(history_retention_days) => patchLimits({ history_retention_days })}
      />
      <NumberField
        label="Minimum credits"
        hint="0 = off"
        value={limits.min_credits_remaining}
        min={0}
        onChange={(min_credits_remaining) => patchLimits({ min_credits_remaining })}
      />

      <SectionHead
        title="Moderation"
        /* Only the blocklist: `locked` also lives in this section of the
           document, but it is the panic button — a reset must not unlock TTS. */
        onReset={() =>
          setDraft({
            ...draft,
            moderation: { ...draft.moderation, blocklist: [] },
          })
        }
      />
      {/* The tenant-wide half of the preset lock. The other half is per
          character, in the Characters tab — this one decides whether anybody
          may speak with a voice that is not on that list at all. */}
      <label className="check">
        <input
          type="checkbox"
          checked={draft.moderation.presets_only}
          onChange={(event) =>
            setDraft({
              ...draft,
              moderation: { ...draft.moderation, presets_only: event.target.checked },
            })
          }
        />
        Non-admins can only use characters
      </label>

      <label>
        Blocked words <small>one per line</small>
        <textarea
          rows={4}
          value={draft.moderation.blocklist.join("\n")}
          onChange={(event) =>
            setDraft({
              ...draft,
              moderation: {
                ...draft.moderation,
                blocklist: event.target.value.split("\n"),
              },
            })
          }
        />
      </label>

      <SectionHead
        title="Pronunciation"
        onReset={
          draft.pronunciation.length
            ? () => setDraft({ ...draft, pronunciation: [] })
            : undefined
        }
      />
      <PronunciationEditor
        entries={draft.pronunciation}
        onChange={(pronunciation) => setDraft({ ...draft, pronunciation })}
      />

      <SectionHead title="Appearance" />
      <AccentPicker
        value={draft.appearance.accent}
        onChange={(accent) => setDraft({ ...draft, appearance: { ...draft.appearance, accent } })}
      />

      <SectionHead
        title="Voice defaults"
        onReset={() =>
          patchEleven({ default_model: DEFAULT_MODEL, output_format: DEFAULT_FORMAT })
        }
      />
      <label>
        Default model
        <select
          value={model}
          onChange={(event) => patchEleven({ default_model: event.target.value })}
        >
          {/* A stored id the account no longer offers still has to show as
              itself — silently jumping to the first option would save a model
              the admin never picked. */}
          {model && !models.some((candidate) => candidate.model_id === model) && (
            <option value={model}>{model} (not on this account)</option>
          )}
          {models.map((candidate) => (
            <option key={candidate.model_id} value={candidate.model_id}>
              {candidate.name || candidate.model_id}
            </option>
          ))}
        </select>

      </label>
      <label>
        Audio format
        <select
          value={format}
          onChange={(event) => patchEleven({ output_format: event.target.value })}
        >
          {!OUTPUT_FORMATS.some((candidate) => candidate.id === format) && (
            <option value={format}>{format} (unsupported)</option>
          )}
          {OUTPUT_FORMATS.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.label}
            </option>
          ))}
        </select>

      </label>

      {/* The key is its own transaction: it is verified upstream and saved on
          its own button, so it deliberately sits outside the dirty/save bar
          that governs the settings document. */}
      <SectionHead title="ElevenLabs API key" />
      <div className="key-status">
        <span className={`dot ${keyStatus?.set ? "on" : "off"}`} />
        {keyStatus?.set ? (
          <span>
            Key saved
            {keyStatus.updated_at
              ? ` ${timeAgo(keyStatus.updated_at)}`
              : ""}
          </span>
        ) : (
          <span>No key set</span>
        )}
      </div>
      <div className="field-row">
        <input
          type="password"
          placeholder="sk_…"
          value={keyInput}
          autoComplete="off"
          onChange={(event) => setKeyInput(event.target.value)}
        />
        <button
          className="ghost"
          disabled={!keyInput.trim() || savingKey}
          onClick={saveKey}
        >
          {savingKey ? "Checking…" : "Replace key"}
        </button>
      </div>

      {/* Sticky, and only present when there is something to lose. The form is
          longer than the drawer, so a save button pinned to the bottom of the
          document is one an admin edits their way past and never sees. */}
      <div className="save-bar" hidden={!dirty}>
        <span className="save-bar-note">Unsaved changes</span>
        <button className="ghost" disabled={saving} onClick={() => setDraft(settings)}>
          Discard
        </button>
        <button disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- pronunciation */

const MAX_ENTRIES = 200;

/* Rows of "what is typed → what the voice says". Kept as rows rather than a
   textarea of `a = b` lines: the arrow is a format nobody has to learn, and a
   line with the separator in it — an emote like `=)` — would otherwise be
   ambiguous. Blank rows are fine to leave; the server drops them on save. */
function PronunciationEditor({
  entries,
  onChange,
}: {
  entries: Pronunciation[];
  onChange: (entries: Pronunciation[]) => void;
}) {
  const [filter, setFilter] = useState("");
  const needle = filter.trim().toLowerCase();
  const shown = entries
    .map((entry, index) => ({ entry, index }))
    .filter(
      ({ entry }) =>
        !needle ||
        entry.from.toLowerCase().includes(needle) ||
        entry.to.toLowerCase().includes(needle),
    );

  const patch = (index: number, next: Partial<Pronunciation>) =>
    onChange(entries.map((entry, at) => (at === index ? { ...entry, ...next } : entry)));

  return (
    <div className="pronunciation">
      <p className="muted" style={{ margin: "0 0 8px" }}>
        Replace words before they're spoken.
      </p>
      {entries.length > 8 && (
        <input
          type="search"
          className="pronunciation-filter"
          placeholder={`Search ${entries.length} entries…`}
          aria-label="Search the pronunciation list"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
      )}
      {shown.length > 0 && (
        <div className="pron-rows">
          {shown.map(({ entry, index }) => (
            <div className="pron-row" key={index}>
              <input
                aria-label={`Typed as (entry ${index + 1})`}
                placeholder="Rinu"
                maxLength={64}
                value={entry.from}
                onChange={(event) => patch(index, { from: event.target.value })}
              />
              <span className="pron-arrow" aria-hidden="true">
                →
              </span>
              <input
                aria-label={`Said as (entry ${index + 1})`}
                placeholder="Ree-noo"
                maxLength={128}
                value={entry.to}
                onChange={(event) => patch(index, { to: event.target.value })}
              />
              <button
                type="button"
                className="icon-btn"
                aria-label={`Remove “${entry.from || "blank"}”`}
                title="Remove"
                onClick={() => onChange(entries.filter((_, at) => at !== index))}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="ghost"
        disabled={entries.length >= MAX_ENTRIES}
        onClick={() => {
          setFilter("");
          onChange([...entries, { from: "", to: "" }]);
        }}
      >
        {entries.length >= MAX_ENTRIES ? `The list is full (${MAX_ENTRIES})` : "Add a word"}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------ appearance */

const ACCENT_NAMES: Record<Accent, string> = {
  blue: "Blue",
  violet: "Violet",
  pink: "Pink",
  teal: "Teal",
  green: "Green",
  amber: "Amber",
  red: "Red",
};

/* The panel's accent, for everyone on this instance. Swatches rather than a
   colour picker: every one of these has a light and a dark variant checked
   for contrast against the panel's surfaces, which an arbitrary hex is not. */
function AccentPicker({ value, onChange }: { value: Accent; onChange: (accent: Accent) => void }) {
  return (
    <div className="accent-picker" role="radiogroup" aria-label="Accent colour">
      {ACCENTS.map((accent) => (
        <button
          key={accent}
          type="button"
          role="radio"
          aria-checked={value === accent}
          className={"swatch" + (value === accent ? " on" : "")}
          data-swatch={accent}
          title={ACCENT_NAMES[accent]}
          onClick={() => onChange(accent)}
        >
          <span className="sr-only">{ACCENT_NAMES[accent]}</span>
        </button>
      ))}

    </div>
  );
}
